// Core
import { useState, useEffect, useCallback, useMemo } from 'react';
import { useQuery } from 'react-query';
import {
  Button,
  Box,
  Grid,
  Flex,
  Text,
  FormControl,
  Input,
  useColorModeValue,
  useBreakpointValue,
  useColorMode
} from '@chakra-ui/react';

// Accets
import { apiService } from 'services/ApiService';
import { ReactComponent as EditIcon } from 'assets/svg/edit.svg';
import { ReactComponent as CancelIcon } from 'assets/svg/cancel.svg';

// Components
import Card from 'components/Card/Card';
import ErrorCard from 'components/Form/Errors';
import EditField from 'components/Form/EditField';
import EditSelect from 'components/Form/EditSelect';

const Profile = ({ userData }) => {
	const bg = useColorModeValue('white', '#0A1027');

	const { colorMode } = useColorMode();
    const isDark = colorMode === 'dark';

	const [object, setObject] = useState({
		firstName: userData.firstName,
		lastName: userData.lastName,
		gender: userData.gender,
		birthDate: userData.birthDate
	});
	const [objectErrors, setObjectErrors] = useState([]);
	const [errorCard, setErrorCard] = useState();

	const setData = useCallback((maxValue, target, key) => {
		if (target.length < maxValue) {
		  setObjectErrors([...objectErrors, key]);
		} else {
		  setObjectErrors(objectErrors.filter(error => error !== key));
		}
		setObject({...object, [key]: target}); 
	}, [object, objectErrors, setObject, setObjectErrors]);

	const handleField = useMemo(() => {
		const fieldConfigs = [
		  { field: 'firstName', maxValue: 1 },
		  { field: 'lastName', maxValue: 1 },
		  { field: 'gender', maxValue: 1 },
		  { field: 'birthDate', maxValue: 0 },
		];

		setErrorCard();

		return fieldConfigs.reduce((acc, { field, maxValue }) => {
		  acc[field] = (e) => setData(maxValue, e.target.value, field);
		  return acc;
		}, {});
	}, [setData]);

	const handleUpdateObject = useCallback(async () => {
		if (objectErrors.length > 0) {
	      setErrorCard(true);
	      return;
	    }

		try {
			const response = await apiService.updateUser(object);
			if(response.data === false || response.statusCode !== 200){
				setErrorCard(response.message);
				return;
			}
		} catch(e){
			setErrorCard(true);
			return;
		}

		setEditMode(false);
	});

	const [editMode, setEditMode] = useState(false);

	const isMobile = useBreakpointValue({ base: true, md: false });
 
	return (
		<Box mt="8px">
			{ errorCard && ( <ErrorCard error={errorCard} /> )}

			<Card padding={{base: '16px 12px 16px 12px', md: '16px 16px 44px 16px'}} bg={bg} overflow="hidden" style={ isDark ? {} : { boxShadow: 'unset'} }>
				<Flex alignItems="center" p="0 0 0 18px" mb="24px" justifyContent="space-between">
					<Text variant="title-lg" fontSize="18px" fontStyle="normal" fontWeight="800" letterSpacing="-1px" textTransform="uppercase">
						Profile
					</Text>

					<>
						{ editMode ? (
							<Flex gap="12px">
								<Button onClick={() => setEditMode(!editMode)} variant="delete-md" w={{base: '44px', md: '88px'}}>{isMobile ? (<CancelIcon />) : 'Cancel'}</Button>
								<Button onClick={handleUpdateObject}  variant="button-md" w={{base: '72px', md: '124px'}}>Save</Button>
							</Flex>
						) : (
							<Button onClick={() => setEditMode(!editMode)} variant="solid-md" w="88px">Edit <EditIcon fontSize="24px" style={{marginLeft: '6px'}} /></Button>
						)}
					</>
				</Flex>

				<Grid mt="2px" alignItems="center" templateColumns={{ sm: '1fr', lg: '1fr 1fr' }} gap="16px" rowGap="44px" templateRows='1fr' zIndex="2">
			        <EditField editMode={editMode} id="firstName" label="First Name" type="text" value={object.firstName} isValid={true} onChange={handleField.firstName} />
			        <EditField editMode={editMode} id="lastName" label="Last Name" type="text" value={object.lastName} isValid={true} onChange={handleField.lastName} />
			        <EditField editMode={editMode} id="birthDate" label="Birthday" type="date" value={object.birthDate} isValid={true} onChange={handleField.birthDate} />
			        <EditSelect 
			        	editMode={editMode}
			        	options={[
			        		{ value: 'Male', title: 'Male' },
			        		{ value: 'Female', title: 'Female' },
			        		{ value: 'Transgender', title: 'Transgender' },
			        		{ value: 'Unknown', title: 'Unknown' },
			        	]}
			        	id="gender" value={object.gender} label="Gender" onChange={handleField.gender} placeholder="Gender" isValid={true}  />
				</Grid>
			</Card>
		</Box>
	);
};


export default Profile;